import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, caseStudySchema } from "../lib/seo";

const AreniaCasinosCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies/arenia-casinos")}
        schema={[
          caseStudySchema("/case-studies/arenia-casinos"),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: "Arenia Casinos", path: "/case-studies/arenia-casinos" },
        ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/case-studies"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
                data-testid="back-to-case-studies-link"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Case Studies
              </Link>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Arenia Casinos: Transforming VIP Loyalty Rewards with WristCoin
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage:
                    "url('/images/case-studies/arenia-casino.jpg')",
                }}
                role="img"
                aria-label="Arenia Casinos"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Overview</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Arenia Casinos rewards its frequent VIP players with complimentary food and beverages as part of its membership perks packages and their loyalty rewards program. The casinos had been using paper tickets and vouchers marked to expire at the end of each day, however there were many problems with that system which WristCoin solved. The main problem was that unused vouchers were being returned to staff who then used them to ring up sales for non VIP players while keeping the cash they'd received. There are various reward levels: Silver, Gold, Platinum, and Black levels, with each level receiving a higher amount of reward points each visit.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Approach</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Frequent VIP players were given WristCoin cashless cards that they'd keep, using them each time they visited the casino rather than having to collect expiring vouchers from the guest services desk each visit. Using WristCoin's reward/loyalty points wallet built into every venue card/wristband, the casino owners would know exactly how many complimentary beverages were served, by what staff member, and to what VIP player. Additional control over rewarding of complimentary items was also attained by the management by authorizing game supervisors to award no more than a specified allowance of points each shift to the most engaged highest spending players at their discretion.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              WristCoin's backoffice web dashboard has an integration with a USB wristband scanner for fast and easy account lookup. By integrating this directly into the web browsers there was no need for IT staff to authorize a new desktop app, reducing the burden to use WristCoin at large venues. These wristband scanners were used to quickly issue new rewards cards to guests, view transaction histories, and look up guest profiles easily without manually entering wristband identifiers.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Outcome</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              In collaboration with our local partner Bandify, WristCoin's reward card program was deployed at all three casino locations in the region: Tijuana, Mexicali, and Morelia. The improved reward cards system eliminated losses resulting from staff using paper vouchers as payment for non-reward guests. By tracking and limiting what additional rewards were granted by supervisors the management gained further control over the number of complimentary drinks and meals dispensed.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              WristCoin tracks exactly which items were rewarded and to which guests, and this data was a powerful insight for the management. In partnership with Bandify, we created rich reporting dashboards that visualized all guest spending habits and patterns as shown below.
            </p>

            <img
              src="/images/case-studies/arenia-casino-dashboard.png"
              alt="arenia casino loyalty rewards and guest information dashboard"
              className="w-full rounded-lg shadow-2xl mb-10"
            />

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Conclusion</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              The introduction of WristCoin at Arenia Casinos has drastically improved VIP player rewards management, effectively addressing the challenges posed by the previous paper voucher system. By issuing cashless cards to frequent VIP players, the casino has eliminated the misuse of unused vouchers by staff, ensuring that rewards are exclusively enjoyed by VIP members. Through integration with a user-friendly web dashboard and wristband scanners, management now has real-time insights into guest spending habits and preferences, facilitating informed decision-making and enhancing operational efficiency.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              The collaboration with Bandify has enabled seamless deployment of the reward card program across all three casino locations, resulting in tangible benefits such as the elimination of losses associated with voucher misuse and enhanced control over reward distribution. The detailed data captured by WristCoin not only empowers management to monitor and regulate complimentary offerings but also provides valuable insights for strategic planning and customer relationship management.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default AreniaCasinosCaseStudy;
